export interface ParsedApiError {
  message: string;
  field?: string;
  code?: string;
  status?: number;
}

interface ApiErrorData {
  error?: string;
  message?: string;
  code?: string;
  details?: { field?: string; [key: string]: unknown };
}

interface AxiosLikeError {
  isAxiosError?: boolean;
  message?: string;
  response?: {
    data?: ApiErrorData;
    status?: number;
  };
}

const isAxiosLikeError = (error: unknown): error is AxiosLikeError =>
  Boolean(
    error &&
      typeof error === "object" &&
      ((error as { isAxiosError?: unknown }).isAxiosError === true || "response" in error),
  );

/**
 * Типизированная обработка ошибок API (§18.4).
 *
 * Маппит HTTP-статусы и коды из apperrors в человекочитаемые сообщения.
 * Поддерживает field-level ошибки валидации для подсветки полей формы.
 */
export function parseApiError(error: unknown): ParsedApiError {
  if (isAxiosLikeError(error)) {
    const data = error.response?.data as ApiErrorData | undefined;
    const status = error.response?.status;
    const code = data?.code;
    const serverMessage = data?.error || data?.message;
    const field = data?.details?.field as string | undefined;

    if (status === 401) {
      return { message: "Сессия истекла", status, code };
    }
    if (status === 403) {
      return { message: serverMessage || "Нет доступа", status, code };
    }
    if (status === 404) {
      return { message: serverMessage || "Запись не найдена", status, code };
    }
    if (status === 409) {
      return { message: serverMessage || "Запись уже существует", status, code };
    }
    if (status === 429) {
      return { message: serverMessage || "Слишком много запросов", status, code };
    }
    if (code === "VALIDATION_ERROR" || (status === 400 && field)) {
      return { message: serverMessage || "Ошибка валидации", field, status, code };
    }
    if (status === 400) {
      return { message: serverMessage || "Некорректный запрос", status, code };
    }
    if (status && status >= 500) {
      return { message: serverMessage || "Ошибка сервера", status, code };
    }
    if (serverMessage) {
      return { message: serverMessage, status, code };
    }
  }

  if (error instanceof Error) {
    if (error.message === "Network Error") {
      return { message: "Нет соединения с сервером" };
    }
    return { message: error.message };
  }

  if (typeof error === "string" && error.trim()) {
    return { message: error };
  }

  return { message: "Не удалось выполнить запрос" };
}
